/**
 * Contrato HTTP entre el navegador y el Worker.
 *
 * Es la ÚNICA puerta entre `web/` y `server/` (constitución, Art. 8). Ninguno
 * de los dos importa del otro: los dos importan de aquí.
 *
 * Todo borde se valida con Zod, y el tipo sale del esquema — no al revés. Si
 * alguna vez el servidor devuelve algo que no calza, el navegador se entera en
 * el parseo y no tres pantallas más adelante con un `undefined`.
 */

import { z } from "zod";

export const ANALYSIS_TIMEZONE = "America/Bogota" as const;

/** ISO 8601 con el offset fijo de Bogotá. Sin offset no se acepta: una hora ambigua produce un período ambiguo. */
export const bogotaInstant = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}-05:00$/u,
    "Debe ser ISO 8601 con offset -05:00, por ejemplo 2026-09-01T07:00:00-05:00",
  );

/* --------------------------------------------------------------- *
 * Vehículos
 * --------------------------------------------------------------- */

/**
 * Vista pública de un vehículo. Nótese lo que NO está: `authorizedUserIds`.
 * La lista de usuarios de WOX se queda en el servidor (constitución, Art. 4).
 */
export const vehicleSchema = z.object({
  deviceId: z.string(),
  name: z.string().nullable(),
  plate: z.string().nullable(),
  group: z.string().nullable(),
  online: z.string().nullable(),
  active: z.boolean().nullable(),
});
export type Vehicle = z.infer<typeof vehicleSchema>;

export const vehicleListSchema = z.object({
  vehicles: z.array(vehicleSchema),
  total: z.number().int(),
  complete: z.boolean(),
});
export type VehicleList = z.infer<typeof vehicleListSchema>;

/* --------------------------------------------------------------- *
 * Análisis
 * --------------------------------------------------------------- */

export const analysisRequestSchema = z.object({
  deviceId: z.string().min(1),
  fromIso: bogotaInstant,
  toIso: bogotaInstant,
});
export type AnalysisRequest = z.infer<typeof analysisRequestSchema>;

export const detectorReachabilitySchema = z.object({
  detector: z.enum(["acceleration", "braking", "turn", "elevated_speed"]),
  label: z.string(),
  requirement: z.string(),
  reachable: z.boolean(),
  /** La cadencia pasa del techo para medir maniobras. Opcional: respuestas viejas no lo traen. */
  beyondCadenceCeiling: z.boolean().optional(),
});

export const samplingQualitySchema = z.object({
  samples: z.number().int(),
  intervals: z.number().int(),
  medianIntervalSeconds: z.number().nullable(),
  meanIntervalSeconds: z.number().nullable(),
  minIntervalSeconds: z.number().nullable(),
  maxIntervalSeconds: z.number().nullable(),
  duplicateInstants: z.number().int(),
  duplicateInstantPercent: z.number().nullable(),
  regime: z.enum(["HIGH", "MEDIUM", "LOW", "UNKNOWN"]),
  detectors: z.array(detectorReachabilitySchema),
  detectorsBlindedBySampling: z.boolean(),
  explanation: z.string().nullable(),
});

export const provenanceSchema = z.object({
  analysisSignature: z.string(),
  inputDigest: z.string(),
  profileDigest: z.string(),
  engineVersion: z.string(),
  pipelineVersion: z.string(),
});

/**
 * El contrato del motor es grande y ya está tipado en `core/pipeline`. Aquí se
 * acepta como objeto y se refina en el cliente sobre el tipo real: duplicar el
 * esquema entero sería otra copia que mantener sincronizada a mano.
 */
/**
 * Qué perfil se aplicó y por qué. Dos análisis con perfiles distintos no son
 * comparables.
 *
 * `scope` es la parte nueva y contesta **de dónde salió** el umbral, que desde
 * que un perfil puede guardarse para un vehículo concreto ya no se deduce del
 * `profileId`:
 *
 *   VEHICULO    umbrales calibrados solo para este equipo.
 *   CADENCIA    umbrales calibrados para todos los que reporten así.
 *   PROVISIONAL los del código, sin calibrar.
 *
 * Va en el contrato y no se calcula en la pantalla porque la decisión la toma el
 * servidor —es el único que ve a la vez la cadencia medida y las filas activas—
 * y porque el informe descargable tiene que decir lo mismo que la pantalla.
 */
export const profileScopeSchema = z.enum(["VEHICULO", "CADENCIA", "PROVISIONAL"]);
export type ProfileScopeWire = z.infer<typeof profileScopeSchema>;

export const profileSelectionSchema = z.object({
  profileId: z.string(),
  regime: z.enum(["HIGH", "MEDIUM", "LOW"]),
  calibration: z.enum(["CALIBRATED", "UNCALIBRATED"]),
  measuredIntervalSeconds: z.number().nullable(),
  scope: profileScopeSchema,
  /** Versión de la fila aplicada. `null` con los umbrales del código. */
  storedVersion: z.number().int().nullable(),
  /** El equipo dueño del perfil cuando `scope` es VEHICULO. */
  scopeDevice: z.string().nullable(),
  reason: z.string(),
});

/**
 * ¿Se pudo medir cada familia del puntaje en este período?
 *
 * Viaja en el contrato porque la decisión es **una sola** y la toma el servidor,
 * que es el único que tiene a la vez el perfil aplicado, los episodios y las
 * mediciones crudas del período. Antes la tomaban por su cuenta la pantalla y el
 * informe descargable, cada uno con su copia de la regla — la misma forma en que
 * este repositorio ya se equivocó dos veces (`applyThresholds`, `inspectDriving`).
 *
 * La regla y el porqué de cada motivo están en
 * `core/report/component-measurability.ts`; aquí solo se declara la forma.
 */
export const familyMeasurabilitySchema = z.object({
  component: z.enum(["velocidadAlta", "frenadas", "aceleraciones", "giros"]),
  detector: z.enum(["acceleration", "braking", "turn", "elevated_speed"]),
  measured: z.boolean(),
  reason: z.enum([
    "episodes_found",
    "within_observed_range",
    "above_observed_range",
    "unreachable_at_cadence",
    "sample_too_small",
    // ADR-009: cero contado como medición por la referencia de la cadencia.
    "within_regime_range",
  ]),
  /** Umbral aplicado, en la unidad de la familia. Siempre positivo. */
  threshold: z.number().nullable(),
  /** Lo más alto que este vehículo hizo en el período, misma unidad. */
  observedMax: z.number().nullable(),
  observedCount: z.number().int(),
  episodes: z.number().int(),
  /** Con `within_regime_range`: cuántos vehículos de la cadencia alcanzan el umbral, de cuántos. */
  regimeReference: z.object({ firing: z.number().int(), vehicles: z.number().int() }).optional(),
  /** La cadencia de este equipo pasa del techo de maniobras. */
  beyondCadenceCeiling: z.boolean().optional(),
});
export type FamilyMeasurabilityWire = z.infer<typeof familyMeasurabilitySchema>;

/**
 * Hasta dónde alcanzó a llegar el historial.
 *
 * Un período largo sobre un equipo de alta cadencia no cabe en una sola
 * petición. Cuando eso pasa se devuelve lo que sí llegó, y esto lo dice: un
 * análisis sobre media quincena presentado como quincena completa es una cifra
 * equivocada que nadie puede detectar mirándola.
 */
export const coverageSchema = z.object({
  truncated: z.boolean(),
  pages: z.number().int(),
  received: z.number().int(),
  lastTimestamp: z.string().nullable(),
});

export const analysisResponseSchema = z.object({
  analysis: z.record(z.string(), z.unknown()),
  sampling: samplingQualitySchema,
  profileSelection: profileSelectionSchema,
  /**
   * Opcional a propósito, y es lo único opcional de esta respuesta: una pestaña
   * abierta desde antes del despliegue sigue recibiendo la respuesta vieja, y un
   * contrato que la rechazara convertiría una mejora del informe en una pantalla
   * en blanco. Los consumidores tratan la ausencia con
   * `measurabilityFromReachability`, que es el criterio anterior escrito una vez.
   */
  measurability: z.array(familyMeasurabilitySchema).optional(),
  provenance: provenanceSchema,
  vehicle: vehicleSchema,
  coverage: coverageSchema,
});
export type AnalysisResponse = z.infer<typeof analysisResponseSchema>;

/* --------------------------------------------------------------- *
 * Flota por lotes
 *
 * La flota no cabe en una sola petición: se pide por lotes y se retoma con el
 * cursor, que es el último `deviceId` procesado (ver `server/fleet`).
 * --------------------------------------------------------------- */

export const fleetBatchRequestSchema = z.object({
  fromIso: bogotaInstant,
  toIso: bogotaInstant,
  /** `null` o ausente empieza desde el principio. */
  cursor: z.string().nullable().optional(),
  batchSize: z.number().int().min(1).max(5).optional(),
});
export type FleetBatchRequest = z.infer<typeof fleetBatchRequestSchema>;

export const fleetVehicleRowSchema = z.object({
  deviceId: z.string(),
  plate: z.string().nullable(),
  name: z.string().nullable(),
  group: z.string().nullable(),
  status: z.enum(["VALID", "PARTIAL", "INSUFFICIENT_DATA"]),
  distanceKm: z.number(),
  coveredHours: z.number(),
  /**
   * Episodios CONSOLIDADOS por familia: la misma magnitud que publica el informe
   * del vehículo, para que la tasa por 100 km de las dos pantallas sea la misma
   * cifra. Antes eran candidatos, que es un número distinto con el mismo nombre.
   */
  episodes: z.object({
    acceleration: z.number().int(),
    braking: z.number().int(),
    turn: z.number().int(),
    elevatedSpeed: z.number().int(),
    total: z.number().int(),
  }),
  severity: z.object({
    moderate: z.number().int(),
    high: z.number().int(),
    critical: z.number().int(),
  }),
  validPercent: z.number().nullable(),
  attributes: z.record(z.string(), z.string().nullable()),
  medianIntervalSeconds: z.number().nullable(),
  regime: z.enum(["HIGH", "MEDIUM", "LOW", "UNKNOWN"]),
  detectorsBlindedBySampling: z.boolean(),
  /**
   * La misma medibilidad por familia que lleva `/api/analysis`, y con el mismo
   * esquema: el ranking necesita saber qué familias quedaron mudas, no solo si la
   * cadencia dejó ciegos a los detectores. Un vehículo con una familia sin medir
   * compite con su tasa calculada sobre las cuatro, y gana por lo que no midió.
   *
   * Opcional por la misma razón que en la respuesta del análisis: una pestaña
   * vieja recibe filas sin el campo y el ranking se degrada al criterio anterior
   * en vez de quedarse en blanco (ver `core/fleet/fleet-comparison.ts`).
   */
  measurability: z.array(familyMeasurabilitySchema).optional(),
  profileId: z.string(),
  calibration: z.enum(["CALIBRATED", "UNCALIBRATED"]),
  analysisSignature: z.string(),
});

/**
 * Un vehículo que no se pudo analizar. Va aparte y con motivo: si se cayera en
 * silencio, la flota parecería más chica de lo que es y nadie lo notaría.
 */
export const fleetFailureSchema = z.object({
  deviceId: z.string(),
  plate: z.string().nullable(),
  reason: z.string(),
});

const batchProgress = {
  /** `null` cuando ya se recorrió la flota entera. */
  nextCursor: z.string().nullable(),
  total: z.number().int(),
  processed: z.number().int(),
};

export const fleetBatchResponseSchema = z.object({
  rows: z.array(fleetVehicleRowSchema),
  failures: z.array(fleetFailureSchema),
  ...batchProgress,
});
export type FleetBatchResponse = z.infer<typeof fleetBatchResponseSchema>;

/* --------------------------------------------------------------- *
 * Laboratorio de calibración
 * --------------------------------------------------------------- */

export const calibrationBatchRequestSchema = fleetBatchRequestSchema;
export type CalibrationBatchRequest = z.infer<typeof calibrationBatchRequestSchema>;

export const candidateMeasurementsSchema = z.object({
  longitudinalMps2: z.array(z.number()),
  yawDegreesPerSecond: z.array(z.number()),
  turnReferenceSpeedKmh: z.array(z.number()),
  // Con `.default([])` para poder leer una respuesta de lote anterior a este
  // campo sin rechazarla. La consecuencia de que falte está acotada y escrita
  // en `component-measurability.ts`: sin intervalo por giro, esa familia se
  // juzga con el criterio de antes en vez de inventarle uno.
  turnIntervalSeconds: z.array(z.number()).default([]),
  segmentSpeedKmh: z.array(z.number()),
  midpointIntervalSeconds: z.array(z.number()),
  eligibleSegments: z.number().int(),
  eligiblePairs: z.number().int(),
});

export const calibrationSampleSchema = z.object({
  deviceId: z.string(),
  plate: z.string().nullable(),
  medianIntervalSeconds: z.number().nullable(),
  measurements: candidateMeasurementsSchema,
});
export type CalibrationSampleWire = z.infer<typeof calibrationSampleSchema>;

export const calibrationBatchResponseSchema = z.object({
  samples: z.array(calibrationSampleSchema),
  failures: z.array(fleetFailureSchema),
  ...batchProgress,
});
export type CalibrationBatchResponse = z.infer<typeof calibrationBatchResponseSchema>;

/* --------------------------------------------------------------- *
 * Banco de pruebas: un vehículo, sus tramos, y los umbrales en vivo
 * --------------------------------------------------------------- */

export const calibrationVehicleRequestSchema = z.object({
  deviceId: z.string().min(1),
  fromIso: bogotaInstant,
  toIso: bogotaInstant,
});
export type CalibrationVehicleRequest = z.infer<typeof calibrationVehicleRequestSchema>;

/** Los umbrales que se pueden mover. Todo lo demás del perfil queda fijo. */
export const tunableThresholdsSchema = z.object({
  accelerationThresholdMps2: z.number().min(0.01).max(15),
  brakingThresholdMps2: z.number().min(-15).max(-0.01),
  minimumSpeedChangeKmh: z.number().min(0).max(100),
  minimumYawRateDegreesPerSecond: z.number().min(0.01).max(180),
  /*
   * Criterio de aceleración lateral del manual. `.default(0)` no es adorno:
   * los perfiles ya guardados en la base no traen este campo, y sin el valor
   * por defecto dejarían de leerse y la calibración activa desaparecería.
   * 0 significa apagado — manda la velocidad angular fija.
   */
  lateralAccelerationMps2: z.number().min(0).max(15).default(0),
  minimumHeadingChangeDegrees: z.number().min(1).max(179),
  minimumReferenceSpeedKmh: z.number().min(0).max(200),
  operationalSpeedLimitKmh: z.number().min(20).max(300),
  /* Histéresis: la racha abierta no se corta hasta bajar de aquí. 0 = apagada,
     y entonces se corta en cuanto la velocidad no supera el límite de entrada.
     Mismo motivo que abajo para el `.default`. */
  operationalSpeedExitKmh: z.number().min(0).max(300).default(0),
  /* Ventana de velocidad sostenida. 0 = apagada. Ver la nota de arriba sobre
     por qué estos campos nuevos llevan `.default`. */
  sustainedWindowSeconds: z.number().min(0).max(120).default(0),
  severityAcceleration: z.tuple([z.number(), z.number(), z.number()]),
  severityBraking: z.tuple([z.number(), z.number(), z.number()]),
  severityYawRate: z.tuple([z.number(), z.number(), z.number()]),
});
export type TunableThresholds = z.infer<typeof tunableThresholdsSchema>;

/* --------------------------------------------------------------- *
 * Verificación de flota de un umbral común de cadencia (ADR-009)
 *
 * Un perfil de alcance CADENCIA ya no se comprueba contra el p99 de un vehículo
 * sino contra la flota de su cadencia. La flota no cabe en una petición, así que
 * el laboratorio la recorre por lotes con los umbrales que quiere guardar; el
 * servidor mide cada vehículo y FIRMA lo que midió. Al guardar, el laboratorio
 * devuelve esas piezas firmadas y el servidor solo cree en las que firmó él, con
 * esos mismos umbrales, esa cadencia y ese período.
 * --------------------------------------------------------------- */

const regimeSchema = z.enum(["HIGH", "MEDIUM", "LOW"]);

export const fleetFiringSchema = z.object({
  velocidadAlta: z.boolean(),
  frenadas: z.boolean(),
  aceleraciones: z.boolean(),
  giros: z.boolean(),
});

export const fleetCheckRequestSchema = fleetBatchRequestSchema.extend({
  regime: regimeSchema,
  thresholds: tunableThresholdsSchema,
});
export type FleetCheckRequest = z.infer<typeof fleetCheckRequestSchema>;

/** Lo que el servidor midió de un vehículo, firmado. Sin placa. */
export const signedFleetEvidenceSchema = z.object({
  version: z.literal(1),
  deviceId: z.string().min(1),
  /** Régimen que la cadencia de ESTE vehículo le asigna; `null` si ninguno. */
  vehicleRegime: regimeSchema.nullable(),
  distanceKm: z.number().min(0),
  firing: fleetFiringSchema,
  /** La verificación a la que pertenece: cadencia, umbrales y período. */
  regime: regimeSchema,
  thresholdsDigest: z.string().regex(/^[0-9a-f]{64}$/u),
  fromIso: bogotaInstant,
  toIso: bogotaInstant,
  issuedAt: z.string(),
  signature: z.string().min(16).max(128),
});
export type SignedFleetEvidence = z.infer<typeof signedFleetEvidenceSchema>;

export const fleetCheckResponseSchema = z.object({
  items: z.array(signedFleetEvidenceSchema),
  failures: z.array(fleetFailureSchema),
  ...batchProgress,
});
export type FleetCheckResponse = z.infer<typeof fleetCheckResponseSchema>;

export const storedProfileSummarySchema = z.object({
  regime: z.enum(["HIGH", "MEDIUM", "LOW"]),
  version: z.number().int(),
  profileId: z.string(),
  profileDigest: z.string(),
  thresholds: tunableThresholdsSchema,
  /** Alcance: `null` es toda la cadencia, un id es solo ese equipo. */
  scopeDevice: z.string().nullable(),
  calibratedFromPlate: z.string().nullable(),
  calibratedFromIso: z.string().nullable(),
  calibratedToIso: z.string().nullable(),
  sampleSegments: z.number().int().nullable(),
  notes: z.string().nullable(),
  calibratedBy: z.string(),
  active: z.boolean(),
  createdAt: z.string(),
});
export type StoredProfileSummary = z.infer<typeof storedProfileSummarySchema>;

/**
 * Qué muestra exacta tiene delante el Laboratorio (`core/calibration/sample-identity.ts`).
 * Dos corridas con huellas distintas no se comparan cifra a cifra.
 */
export const labSampleIdentitySchema = z.object({
  deviceId: z.string(),
  fromIso: z.string(),
  toIso: z.string(),
  timezone: z.string(),
  /** Registros normalizados que entraron al motor. */
  records: z.number().int(),
  /** Registros que devolvió la telemetría antes de normalizar. */
  received: z.number().int(),
  /** `computeInputDigest`: la misma huella que firma los informes. */
  inputDigest: z.string(),
  engineVersion: z.string(),
  pipelineVersion: z.string(),
});
export type LabSampleIdentity = z.infer<typeof labSampleIdentitySchema>;

/**
 * Todo lo que el banco de pruebas necesita de un vehículo.
 *
 * Manda los registros normalizados en crudo, no los tramos ya calculados. Es a
 * propósito: con los registros el navegador puede volver a correr el MISMO
 * motor cada vez que se mueve una perilla, y lo que se ve en la mesa de trabajo
 * es exactamente lo que va a salir en el informe. Mandar tramos precalculados
 * obligaría a reevaluar los umbrales con una segunda implementación, y calibrar
 * contra un motor que no existe es peor que no calibrar.
 */
export const calibrationVehicleResponseSchema = z.object({
  vehicle: vehicleSchema,
  sampling: samplingQualitySchema,
  profileSelection: profileSelectionSchema,
  /** Umbrales de arranque: los calibrados si existen, si no los del código. */
  thresholds: tunableThresholdsSchema,
  thresholdSource: z.enum(["CALIBRADO", "PROVISIONAL"]),
  /** Registros normalizados. El motor corre en el navegador con estos. */
  records: z.array(z.record(z.string(), z.unknown())),
  received: z.number().int(),
  /** Opcional solo para una pestaña abierta antes de este cambio. */
  sampleIdentity: labSampleIdentitySchema.optional(),
});
export type CalibrationVehicleResponse = z.infer<typeof calibrationVehicleResponseSchema>;

export const saveCalibrationRequestSchema = z.object({
  regime: z.enum(["HIGH", "MEDIUM", "LOW"]),
  thresholds: tunableThresholdsSchema,
  /**
   * Alcance de lo que se guarda, y la pregunta que el laboratorio hace al
   * guardar: `VEHICULO` deja la fila activa solo para `calibratedFromDevice`,
   * `CADENCIA` la deja activa para todos los equipos del régimen.
   *
   * Explícito y sin valor por omisión a propósito: el alcance es la decisión más
   * cara de esta pantalla —un umbral urbano aplicado a la flota entera deja de
   * marcar a los de carretera— y un `.default()` la tomaría en silencio.
   */
  scope: z.enum(["VEHICULO", "CADENCIA"]),
  calibratedFromDevice: z.string().nullable(),
  calibratedFromPlate: z.string().nullable(),
  calibratedFromIso: z.string().nullable(),
  calibratedToIso: z.string().nullable(),
  sampleSegments: z.number().int().nullable(),
  notes: z.string().max(500).nullable(),
  /**
   * La verificación de flota firmada, obligatoria con alcance CADENCIA
   * (ADR-009). Con alcance VEHICULO no se usa: ese sigue con la guarda del p99.
   */
  fleetEvidence: z.array(signedFleetEvidenceSchema).max(600).optional(),
  /**
   * Huella de la muestra que mostraba el Laboratorio. Con alcance de vehículo
   * el servidor vuelve a medir y dice si verificó esa misma muestra. No bloquea
   * si difieren: lo dice.
   */
  sampleInputDigest: z
    .string()
    .regex(/^[0-9a-f]{64}$/u)
    .nullable()
    .optional(),
});
export type SaveCalibrationRequest = z.infer<typeof saveCalibrationRequestSchema>;

export const calibrationProfilesResponseSchema = z.object({
  profiles: z.array(storedProfileSummarySchema),
});
export type CalibrationProfilesResponse = z.infer<typeof calibrationProfilesResponseSchema>;

/* --------------------------------------------------------------- *
 * Administrador de usuarios
 * --------------------------------------------------------------- */

export const guardiaRoleSchema = z.enum([
  "GUARDIA_SUPER_ADMIN",
  "GESTOR_GPS",
  "ASEGURADORA",
  "CLIENTE",
  "ORG_ADMIN",
  "ORG_MEMBER",
]);
export type GuardiaRoleName = z.infer<typeof guardiaRoleSchema>;

export const scopeTypeSchema = z.enum(["CARRIER", "PLATE", "DOCUMENT"]);

export const scopeEntrySchema = z.object({
  id: z.number().int(),
  scopeType: scopeTypeSchema,
  value: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
});

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: guardiaRoleSchema,
  displayName: z.string().nullable(),
  gpswoxUserId: z.number().int().nullable(),
  gpswoxStatus: z.enum(["VERIFIED", "PENDING", "REVOKED"]).nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  scopes: z.array(scopeEntrySchema),
});
export type AdminUserView = z.infer<typeof adminUserSchema>;

export const adminUsersResponseSchema = z.object({
  users: z.array(adminUserSchema),
  /** Aseguradoras presentes en la base de atributos. Alimenta el selector. */
  carriers: z.array(z.string()),
});
export type AdminUsersResponse = z.infer<typeof adminUsersResponseSchema>;

export const saveUserRequestSchema = z.object({
  /** Ausente crea; presente actualiza. */
  id: z.string().nullable().optional(),
  email: z.string().email("Escribe un correo válido."),
  role: guardiaRoleSchema,
  displayName: z.string().max(120).nullable(),
  gpswoxUserId: z.number().int().positive().nullable(),
  gpswoxStatus: z.enum(["VERIFIED", "PENDING", "REVOKED"]).nullable(),
  active: z.boolean(),
});
export type SaveUserRequest = z.infer<typeof saveUserRequestSchema>;

export const scopeRequestSchema = z.object({
  userId: z.string().min(1),
  scopeType: scopeTypeSchema,
  value: z.string().min(1).max(120),
});

/* --------------------------------------------------------------- *
 * Atributos del vehículo
 * --------------------------------------------------------------- */

export const ATTRIBUTE_FIELDS = [
  "aseguradora",
  "numero_poliza",
  "marca",
  "linea",
  "modelo_anio",
  "uso_servicio",
  "valor_asegurado",
  "titular_nombre",
  "titular_documento",
  "tipo_persona",
  "genero",
  "fecha_nacimiento",
  "ciudad",
  "departamento",
  "ocupacion",
] as const;

const attributeValues = z.object(
  Object.fromEntries(ATTRIBUTE_FIELDS.map((field) => [field, z.string().nullable().optional()])) as Record<
    (typeof ATTRIBUTE_FIELDS)[number],
    z.ZodOptional<z.ZodNullable<z.ZodString>>
  >,
);

const fieldProvenanceSchema = z.object({
  system: z.enum(["sync", "alegra", "highlevel", "wox", "manual"]),
  field: z.string(),
  observedAt: z.string(),
});

export const vehicleAttributeRowSchema = z.object({
  plate: z.string(),
  deviceId: z.string().nullable(),
  values: attributeValues,
  provenance: z.record(z.string(), fieldProvenanceSchema),
  updatedAt: z.string(),
  updatedBy: z.string(),
});
export type VehicleAttributeRowView = z.infer<typeof vehicleAttributeRowSchema>;

export const attributesResponseSchema = z.object({
  rows: z.array(vehicleAttributeRowSchema),
  /** Placas de la flota que todavía no tienen fila. Es lo que falta por cargar. */
  missingPlates: z.array(z.string()),
});
export type AttributesResponse = z.infer<typeof attributesResponseSchema>;

/**
 * Carga de atributos.
 *
 * Un campo ausente deja lo que hubiera; un `null` explícito borra. La
 * diferencia importa: el sincronizador de pólizas no sabe de ocupación, y si un
 * ausente borrara, cada corrida de Sync vaciaría lo que puso HighLevel.
 */
export const upsertAttributesRequestSchema = z.object({
  entries: z
    .array(
      z.object({
        plate: z.string().min(1),
        deviceId: z.string().nullable().optional(),
        values: attributeValues,
        provenance: z.record(z.string(), fieldProvenanceSchema).optional(),
      }),
    )
    .min(1)
    .max(500),
});
export type UpsertAttributesRequest = z.infer<typeof upsertAttributesRequestSchema>;

/* --------------------------------------------------------------- *
 * Sesión
 * --------------------------------------------------------------- */

export const currentUserSchema = z.object({
  email: z.string(),
  role: guardiaRoleSchema,
  /** `false` significa que no verá ningún vehículo hasta que se verifique. */
  gpswoxLinked: z.boolean(),
  /** Qué puede hacer, resuelto en el servidor. La UI solo lo obedece. */
  can: z.object({
    calibrate: z.boolean(),
    administer: z.boolean(),
    seeCohorts: z.boolean(),
    seeFleet: z.boolean(),
    /** Pestaña Rastreo (motor propio). Solo roles globales mientras no exista el vínculo equipo↔placa. */
    seeRastreo: z.boolean().default(false),
  }),
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const magicLinkRequestSchema = z.object({
  email: z.string().email("Escribe un correo válido."),
});
export type MagicLinkRequest = z.infer<typeof magicLinkRequestSchema>;

/** La respuesta es idéntica exista o no el correo: no revela quién está registrado. */
export const magicLinkAcceptedSchema = z.object({
  ok: z.literal(true),
  message: z.string(),
});

/* --------------------------------------------------------------- *
 * Errores
 * --------------------------------------------------------------- */

export const apiErrorSchema = z.object({
  error: z.string(),
  code: z.string(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/**
 * Códigos de error del contrato. `VEHICLE_SCOPE_DENIED` es deliberadamente
 * indistinguible de «no existe»: la negación no revela si el vehículo existe
 * ni de quién es (constitución, Art. 4).
 */
export const API_ERROR_CODES = {
  UNAUTHENTICATED: "UNAUTHENTICATED",
  VEHICLE_SCOPE_DENIED: "VEHICLE_SCOPE_DENIED",
  INVALID_REQUEST: "INVALID_REQUEST",
  INVALID_PERIOD: "INVALID_PERIOD",
  TELEMETRY_UNAVAILABLE: "TELEMETRY_UNAVAILABLE",
  NOT_CONFIGURED: "NOT_CONFIGURED",
  /**
   * La calibración no se guardó porque no se pudo comprobar que el servidor
   * verificó la muestra que mostraba el Laboratorio: la huella no coincide o no
   * vino. Solo lo emite la política D3 `BLOQUEAR` (ADR-013), hoy inactiva. La
   * pantalla lo usa para ofrecer volver a cargar la muestra.
   */
  SAMPLE_CHANGED: "SAMPLE_CHANGED",
  /** Registro de Rastreo: ya existe un equipo con ese IMEI, ese enlace al motor o un cliente con ese documento. */
  CONFLICT: "CONFLICT",
  /** Registro de Rastreo: el equipo que se quiere editar no está registrado. */
  NOT_FOUND: "NOT_FOUND",
} as const;
